import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import io from "socket.io-client";
import {
  apiEntitySearch,
  apiPosCancelTerminalCommand,
  apiPosCreateEnrollmentCode,
  apiPosGetTerminal,
  apiPosGetVendorOutlets,
  apiPosListEnrollmentCodes,
  apiPosListTerminals,
  apiPosQueueTerminalCommand,
  apiPosRevokeEnrollmentCode,
  apiPosRotateTerminalCredential,
  apiPosUpdateTerminal,
  dashboardApiBaseUrl,
} from "../api";
import { useCountry } from "../context/CountryContext";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils";
import "../assets/css/pos-terminal-fleet.css";
import ConfirmDialog, { useConfirmDialog } from "../components/ConfirmDialog";
import AppSelect from "../components/ui/AppSelect";

const EMPTY_FILTERS = {
  search: "",
  vendorType: "",
  status: "",
  connectivity: "",
};

const relativeTime = (value) => {
  if (!value) return "Never";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Unknown";
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 45) return "Just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.round(seconds / 86400)} days ago`;
  return new Date(value).toLocaleDateString();
};

const fullDate = (value) =>
  value ? new Date(value).toLocaleString() : "Not reported";

/**
 * How long until a FUTURE moment — an enrollment code's expiry.
 *
 * `relativeTime` above measures how long ago something happened and clamps the
 * elapsed seconds at 0, so a timestamp 24 hours in the future came out as
 * "Just now" — which reads as "this code has just expired", the exact opposite
 * of the truth, on the one column an operator checks before reading a code down
 * a phone line.
 */
const timeUntil = (value) => {
  if (!value) return "No expiry";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Unknown";
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  if (seconds <= 0) return "Expired";
  if (seconds < 60) return "Under a minute";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min left`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr left`;
  return `${Math.round(seconds / 86400)} days left`;
};

const profileLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const vendorTypeLabel = (value) => {
  const labels = {
    RETAILER: "Retailer",
    WHOLESALER: "Wholesaler",
    RESTAURANT: "Restaurant",
    HEALTHCARE: "Healthcare",
    BRAND_OWNER: "Brand owner",
    EVENT_ORGANIZER: "Event organizer",
  };
  return labels[value] || profileLabel(value) || "Unassigned";
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * Why this command cannot be sent to THIS terminal right now — or "" if it can.
 *
 * A deliberate mirror of `commandAvailability` in posTerminalService.js. The
 * server refuses regardless; this exists so the reason is on the button before
 * the click, rather than as a 409 afterwards. In particular a build that does
 * not know a command type answers it "Unsupported command" at best, so offering
 * the button at all would have ops believing a terminal was rebooted when it
 * never was.
 */
const commandBlockReason = (capability, terminal) => {
  const reported = terminal?.reportedState || {};
  const reportedVersion = Number(reported.appVersionCode || 0);
  const minimum = Number(capability?.minAppVersionCode || 0);
  if (minimum > 0 && reportedVersion > 0 && reportedVersion < minimum) {
    return `Needs POS build ${minimum} — this terminal reports ${reportedVersion}`;
  }
  if (capability?.blockedByActiveShift && reported.activeShift === true) {
    return "Close the open shift first";
  }
  return "";
};

/**
 * One sentence of facts about the shift that is blocking an assignment change:
 * who left it open, since when, and when the terminal was last heard from.
 * Shared by the pre-click notice in the assignment section and the 409
 * feedback after a save, so the two never describe the same drawer differently.
 */
const shiftLockSummary = (shift, terminal) => {
  const cashier = shift?.cashierName ? `${shift.cashierName}'s shift` : "A shift";
  const openedAt = shift?.openedAt ? ` opened ${fullDate(shift.openedAt)}` : "";
  const lastSeen = shift?.lastSeenAt || terminal?.lastSeenAt;
  const seen = lastSeen
    ? ` and the terminal was last heard from ${relativeTime(lastSeen)}`
    : " and the terminal has never reported in";
  return `${cashier}${openedAt} is still open on this terminal${seen}.`;
};

/**
 * A switch that cannot be flipped explains itself. `reason` renders under the
 * hint at full contrast when `disabled` is set; before this, the Order
 * fulfilment and Ticket validation rows were greyed out with nothing on them,
 * and "nothing happens when I tap it" was the bug report (Sep 2 2026).
 */
const ToggleRow = ({ checked, onChange, title, hint, disabled, tooltip, reason }) => (
  <label
    className={`pos-fleet-toggle-row${disabled ? " is-disabled" : ""}`}
    title={tooltip || (disabled ? reason : undefined) || undefined}
  >
    <span>
      <strong>{title}</strong>
      <small>{hint}</small>
      {disabled && reason ? (
        <small className="pos-fleet-toggle-reason">{reason}</small>
      ) : null}
    </span>
    <span className="pos-fleet-switch">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
      />
      <span />
    </span>
  </label>
);

/**
 * Which vendor types may switch each capability on. The server accepts the
 * flags for any profile; this is the console's own guard, and its reasons are
 * shown on the switch. Order fulfilment: restaurants and, since Sep 2 2026,
 * brand owners (the POS accept path now moves a brand order's own stage, see
 * services/brandOrderStageService.js). Retail, wholesale and healthcare tills
 * sell at the counter and their online orders are worked from the dashboard.
 */
const ORDER_FULFILMENT_VENDOR_TYPES = ["RESTAURANT", "BRAND_OWNER"];

const orderFulfilmentBlockReason = (vendorType, vendorTypeLabel) => {
  if (!vendorType) return "Assign a vendor first.";
  if (ORDER_FULFILMENT_VENDOR_TYPES.includes(vendorType)) return null;
  const who = vendorTypeLabel || "This vendor type's";
  return `${who} terminals sell at the counter; online orders are processed from the dashboard. Order fulfilment runs on restaurant and brand terminals.`;
};

const ticketValidationBlockReason = (vendorType) =>
  vendorType === "EVENT_ORGANIZER"
    ? null
    : "Ticket validation runs on event organizer terminals only.";

const receiptPrintingBlockReason = (vendorType) =>
  vendorType === "EVENT_ORGANIZER"
    ? "A gate terminal admits ticket holders; there is no sale to print."
    : null;

/**
 * The profile control. Profiles follow the vendor's TYPE and every type has
 * exactly one today, so a <select> here was a one-entry dropdown that read as
 * "something failed to load" (Sep 2 2026). With one option it is a plain
 * read-out with the rule spelled out; the select comes back the day a type
 * gains a second profile.
 */
const ProfileField = ({ value, profiles, vendorTypeLabel, onChange }) => {
  if (profiles.length > 1) {
    return (
      <label>
        <span>Terminal profile</span>
        <AppSelect value={value} onChange={(event) => onChange(event.target.value)}>
          {profiles.map((profile) => (
            <option key={profile} value={profile}>
              {profileLabel(profile)}
            </option>
          ))}
        </AppSelect>
      </label>
    );
  }
  const only = profiles[0] || value;
  return (
    <div className="pos-fleet-profile-fixed">
      <span>Terminal profile</span>
      <strong>{profileLabel(only) || "Unassigned"}</strong>
      <small>
        Profiles follow the vendor's type
        {vendorTypeLabel ? ` (${vendorTypeLabel.toLowerCase()})` : ""}. To run
        a different one, assign the terminal to a vendor of that type:
        restaurants get Restaurant Fulfilment, event organizers Event Access,
        pharmacies Healthcare Checkout, shops Retail Checkout.
      </small>
    </div>
  );
};

const SummaryCard = ({ label, value, tone, detail }) => (
  <div className={`pos-fleet-summary-card tone-${tone}`}>
    <div className="pos-fleet-summary-label">{label}</div>
    <div className="pos-fleet-summary-value">{value}</div>
    <div className="pos-fleet-summary-detail">{detail}</div>
  </div>
);

const FACTORY_COMMANDS = {
  verify: `adb devices
adb shell dumpsys account | grep -c "Account {"`,
  download: `curl -fL \\
  https://lusuku-prod-server.herokuapp.com/downloads/pos/lusuku-pos.apk \\
  -o /private/tmp/lusuku-pos.apk

adb install -r /private/tmp/lusuku-pos.apk`,
  owner: `adb shell dpm set-device-owner \\
  ug.lusuku.pos/.kiosk.LusukuDeviceAdminReceiver`,
  launch: `adb shell monkey \\
  -p ug.lusuku.pos \\
  -c android.intent.category.LAUNCHER 1`,
  // A ROM without a setup wizard marks the device provisioned at boot, so dpm
  // refuses Device Owner permanently. Clearing these two puts it back in the
  // state dpm expects.
  clearProvisioned: `adb shell settings put global device_provisioned 0
adb shell settings put secure user_setup_complete 0`,
  script: `cd "/Users/Mydhe Files/Lusuku_Mosh_Expo_MacApp/Lusuku_POS/scripts"
./provision-terminal.sh`,
  // A locked kiosk puts Settings out of reach, so automatic time has to be set
  // while the cable is still attached. A wrong clock fails every certificate.
  autoTime: `adb shell settings put global auto_time 1
adb shell settings put global auto_time_zone 1
adb shell date`,
  verifyRelease: `curl -fsS https://lusuku-prod-server.herokuapp.com/api/pos/health
curl -fsS https://lusuku-prod-server.herokuapp.com/api/pos/app-version`,
  // The terminal resolves through whatever DNS its Wi-Fi advertises. When a
  // router hands out third-party resolvers that answer with a non-Heroku
  // address, TLS fails correctly and the app says it cannot verify the
  // connection. These two lines must print the same address.
  dnsCheck: `adb shell ping -c 1 lusuku-prod-server.herokuapp.com
dig +short lusuku-prod-server.herokuapp.com`,
  dnsFix: `cd "/Users/Mydhe Files/Lusuku_Mosh_Expo_MacApp/Lusuku_POS/scripts"
./fix-terminal-dns.sh`,
  // One pass over every cause of a failed sign-in: clock skew, captive portal,
  // the ROM's CA store, the chain as actually served, installed version, and a
  // logcat capture. Run this before forming a theory.
  diagnose: `cd "/Users/Mydhe Files/Lusuku_Mosh_Expo_MacApp/Lusuku_POS/scripts"
./diagnose-terminal.sh`,
};

const CopyableCommand = ({ command, label = "Copy command" }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(command);
    } catch (_error) {
      const textarea = document.createElement("textarea");
      textarea.value = command;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };

  return (
    <div className="pos-fleet-command-block">
      <pre>
        <code>{command}</code>
      </pre>
      <button type="button" onClick={copy} aria-label={label}>
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const posApkUrl = () => `${dashboardApiBaseUrl()}/downloads/pos/lusuku-pos.apk`;

/**
 * The adb half of provisioning cannot move into a browser — a web page has no
 * USB. What it CAN do is stop asking an operator to assemble six commands by
 * hand: this bakes the release URL, the terminal's name and its enrollment code
 * into one script, and turns the account-count precondition from something you
 * eyeball into something the script asserts and refuses to continue past.
 */
const buildProvisionScript = ({ code, label, country, expiresAt }) => `#!/usr/bin/env bash
set -euo pipefail
# Lusuku POS — provision "${label || "New terminal"}"  (${country || "UG"})
# Enrollment code: ${code}
# Expires: ${expiresAt ? new Date(expiresAt).toLocaleString() : "unknown"}

adb devices
accounts="$(adb shell dumpsys account | grep -c 'Account {' || true)"
if [ "$accounts" != "0" ]; then
  echo "This device has $accounts Google account(s). Factory-reset it first —"
  echo "Device Owner cannot be set while any account exists."
  exit 1
fi

curl -fL "${posApkUrl()}" -o /tmp/lusuku-pos.apk
adb install -r /tmp/lusuku-pos.apk
adb shell dpm set-device-owner ug.lusuku.pos/.kiosk.LusukuDeviceAdminReceiver
adb shell monkey -p ug.lusuku.pos -c android.intent.category.LAUNCHER 1

echo
echo "Now on the terminal: Set Up Terminal → Enrollment code → ${code}"
`;

/** Large, spaced and copyable — this is a code somebody reads down a phone. */
const CodeDisplay = ({ code, tone = "default" }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (_error) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };
  return (
    <div className={`pos-fleet-code-display tone-${tone}`}>
      <code>{code}</code>
      <button type="button" onClick={copy}>
        {copied ? "Copied" : "Copy code"}
      </button>
    </div>
  );
};

const CODE_TTL_OPTIONS = [
  { value: 60, label: "1 hour" },
  { value: 1440, label: "24 hours" },
  { value: 4320, label: "3 days" },
  { value: 10080, label: "7 days" },
];

/**
 * Provision-terminal wizard.
 *
 * Four steps, and the ordering is the point: every assignment decision is made
 * HERE, by the person who knows the shop, before any hardware is touched. The
 * terminal then arrives already named, assigned, configured and country-scoped,
 * and no vendor password was ever typed into it.
 */
const ProvisionWizard = ({ vendorTypes, onClose, onMinted }) => {
  const [step, setStep] = useState(1);
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [form, setForm] = useState({
    terminalProfile: "",
    outletId: "",
    eventOrganizerId: "",
    label: "",
    ttlMinutes: 1440,
    maxUses: 1,
    note: "",
    desiredConfig: {},
  });
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState("");
  const [minted, setMinted] = useState(null);
  const searchTimer = useRef(null);

  const typeMeta = vendorTypes.find((item) => item.value === vendor?.operationMode);
  const profiles = typeMeta?.profiles || [];

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const query = vendorQuery.trim();
    if (query.length < 2) {
      setVendorResults([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await apiEntitySearch("VENDOR", query, 12);
        const supported = new Set(vendorTypes.map((item) => item.value));
        setVendorResults(
          (data.results || []).filter((item) => supported.has(item.operationMode))
        );
      } catch (_error) {
        setVendorResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [vendorQuery, vendorTypes]);

  useEffect(() => {
    // Every vendor type with an outlet collection can be scoped now (R1/H3,
    // WIRE_CONTRACT §7) — the mint path enforces ownership rather than vendor
    // type. Only event organizers are skipped: a gate is not an outlet.
    if (!vendor || vendor.operationMode === "EVENT_ORGANIZER") {
      setOutlets([]);
      return undefined;
    }
    let cancelled = false;
    setOutletsLoading(true);
    apiPosGetVendorOutlets(vendor.id)
      .then(({ data }) => !cancelled && setOutlets(data.outlets || []))
      .catch(() => !cancelled && setOutlets([]))
      .finally(() => !cancelled && setOutletsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [vendor]);

  const pickVendor = (picked) => {
    const meta = vendorTypes.find((item) => item.value === picked.operationMode);
    setVendor(picked);
    setForm((current) => ({
      ...current,
      terminalProfile: meta?.defaultProfile || "",
      outletId: "",
      eventOrganizerId: picked.eventOrganizerId ? String(picked.eventOrganizerId) : "",
      // Prefill from the profile's own defaults so the operator edits a real
      // starting point rather than an empty object the server would fill in.
      desiredConfig: { ...(meta?.defaultConfig || {}) },
    }));
    setVendorQuery("");
    setVendorResults([]);
    setStep(2);
  };

  const patchConfig = (key, value) =>
    setForm((current) => ({
      ...current,
      desiredConfig: { ...current.desiredConfig, [key]: value },
    }));

  const mint = async () => {
    setMinting(true);
    setError("");
    try {
      const { data } = await apiPosCreateEnrollmentCode({
        vendorUserId: vendor.id,
        terminalProfile: form.terminalProfile,
        outletId: form.outletId || undefined,
        eventOrganizerId: form.eventOrganizerId || undefined,
        label: form.label.trim(),
        ttlMinutes: form.ttlMinutes,
        maxUses: form.maxUses,
        note: form.note.trim(),
        desiredConfig: form.desiredConfig,
      });
      setMinted(data.enrollmentCode);
      setStep(4);
      onMinted?.();
    } catch (mintError) {
      setError(
        errorMessage(mintError, "Could not create the enrollment code.")
      );
    } finally {
      setMinting(false);
    }
  };

  const vendorType = vendor?.operationMode || "";
  // E3 / WIRE_CONTRACT §11 — an EVENT_ACCESS terminal may not be provisioned
  // without the door it stands at. The wizard blocks the step rather than
  // letting the operator find out from a 400 two screens later.
  const gateLabelMissing =
    form.terminalProfile === "EVENT_ACCESS" &&
    !String(form.desiredConfig.gateLabel || "").trim();

  return (
    <div
      className="pos-fleet-inspector-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <aside className="pos-fleet-wizard" aria-label="Provision a terminal">
        <div className="pos-fleet-inspector-head">
          <div>
            <span>Provision a terminal</span>
            <h2>{minted ? "Hand this to the operator" : "New terminal"}</h2>
            <small>
              {minted
                ? "The device binds itself when the code is typed."
                : "No vendor password is typed on the hardware."}
            </small>
          </div>
          <button type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <ol className="pos-fleet-wizard-steps">
          {["Vendor", "Profile", "Label & validity", "Hand-off"].map(
            (title, index) => (
              <li
                key={title}
                className={
                  step === index + 1
                    ? "is-active"
                    : step > index + 1
                      ? "is-done"
                      : ""
                }
              >
                <i>{index + 1}</i>
                <span>{title}</span>
              </li>
            )
          )}
        </ol>

        <div className="pos-fleet-wizard-body">
          {error && <div className="pos-fleet-alert is-error">{error}</div>}

          {step === 1 && (
            <section className="pos-fleet-form-section">
              <div className="pos-fleet-section-title">
                <div>
                  <h3>Which vendor is this terminal for?</h3>
                  <p>
                    The code carries this decision, so the terminal is assigned
                    the moment it enrolls — nothing to remember afterwards.
                  </p>
                </div>
              </div>
              <label className="pos-fleet-vendor-search">
                <span>Find a vendor</span>
                <div>
                  <input
                    autoFocus
                    value={vendorQuery}
                    onChange={(event) => setVendorQuery(event.target.value)}
                    placeholder="Search vendor name or phone"
                  />
                  {searching && <Spinner animation="border" size="sm" />}
                </div>
              </label>
              {vendorResults.length > 0 && (
                <div className="pos-fleet-vendor-results">
                  {vendorResults.map((item) => (
                    <button type="button" key={item.id} onClick={() => pickVendor(item)}>
                      <strong>{item.label}</strong>
                      <span>
                        {/* The server's subLabel repeats the raw enum
                            ("BRAND_OWNER · +256…"), so build the line from the
                            fields instead of appending it. */}
                        {vendorTypeLabel(item.operationMode)}
                        {item.phone ? ` · ${item.phone}` : ""}
                        {item.verified === false ? " · NOT VERIFIED" : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {vendorQuery.trim().length >= 2 &&
                !searching &&
                vendorResults.length === 0 && (
                  <p className="pos-fleet-wizard-hint">
                    No POS-eligible vendor matches that. Only retailers,
                    wholesalers, restaurants, healthcare vendors, brand owners
                    and event organizers can hold a terminal.
                  </p>
                )}
            </section>
          )}

          {step === 2 && vendor && (
            <section className="pos-fleet-form-section">
              <div className="pos-fleet-current-assignment">
                <span>{vendorTypeLabel(vendorType)}</span>
                <strong>{vendor.label}</strong>
                <small>{typeMeta?.useCase || ""}</small>
              </div>
              {vendor.verified === false && (
                <div className="pos-fleet-alert is-warning with-top-gap">
                  <strong>This vendor is not verified yet.</strong> The code will
                  mint, and the terminal will enrol — but the catalog pull is
                  refused with <code>SELLER_NOT_VERIFIED</code>, so the till
                  arrives empty and the operator sees only a message about the
                  account. Verify the account first, or expect to re-pull the
                  snapshot afterwards.
                </div>
              )}
              <div className="pos-fleet-field-grid with-top-gap">
                <ProfileField
                  value={form.terminalProfile}
                  profiles={profiles}
                  vendorTypeLabel={typeMeta?.label}
                  onChange={(value) =>
                    setForm((c) => ({ ...c, terminalProfile: value }))
                  }
                />
                {vendorType !== "EVENT_ORGANIZER" &&
                  (outletsLoading || outlets.length > 0) && (
                  <label>
                    <span>Outlet (optional)</span>
                    <AppSelect
                      value={form.outletId}
                      disabled={outletsLoading}
                      onChange={(event) =>
                        setForm((c) => ({ ...c, outletId: event.target.value }))
                      }
                    >
                      <option value="">
                        {outletsLoading ? "Loading outlets…" : "All outlets"}
                      </option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {outlet.name}
                        </option>
                      ))}
                    </AppSelect>
                    <small>
                      Scopes this terminal's catalog and order feed to one
                      branch. Leave on "All outlets" for a single-site vendor.
                    </small>
                  </label>
                )}
              </div>
              <div className="with-top-gap">
                <ToggleRow
                  title="Kiosk lock"
                  hint="Keep the terminal pinned to Lusuku POS."
                  checked={form.desiredConfig.kioskLocked !== false}
                  onChange={(value) => patchConfig("kioskLocked", value)}
                />
                <ToggleRow
                  title="Automatic receipt printing"
                  hint="Print when checkout completes."
                  checked={form.desiredConfig.autoPrintReceipts !== false}
                  onChange={(value) => patchConfig("autoPrintReceipts", value)}
                  disabled={vendorType === "EVENT_ORGANIZER"}
                  reason={receiptPrintingBlockReason(vendorType)}
                />
                <ToggleRow
                  title="Order fulfilment"
                  hint="Accept, prepare and hand off incoming online orders on the terminal."
                  checked={form.desiredConfig.allowOrderFulfillment === true}
                  onChange={(value) => patchConfig("allowOrderFulfillment", value)}
                  disabled={!ORDER_FULFILMENT_VENDOR_TYPES.includes(vendorType)}
                  reason={orderFulfilmentBlockReason(vendorType, typeMeta?.label)}
                />
                <ToggleRow
                  title="Ticket validation"
                  hint="Enable event ticket scanning and gate admission."
                  checked={form.desiredConfig.allowTicketScanning === true}
                  onChange={(value) => patchConfig("allowTicketScanning", value)}
                  disabled={vendorType !== "EVENT_ORGANIZER"}
                  reason={ticketValidationBlockReason(vendorType)}
                />
                {vendorType === "EVENT_ORGANIZER" && (
                  <>
                    {/*
                      The same disabled row and the same words the edit drawer
                      carries (E2). A terminal being provisioned has reported
                      nothing yet, so there is no build that can have earned
                      this — it is off here by construction, not by policy, and
                      the server refuses it with 409 either way.
                    */}
                    <ToggleRow
                      title="Ticket sales — pending terminal build"
                      hint="Box-office selling is not in the terminal app yet. Gate validation above is the supported event workflow today."
                      tooltip="The server side is built and tested, but the Android box-office screen has not shipped. A terminal unlocks this by reporting the capability once the release that adds it is installed."
                      checked={false}
                      onChange={() => {}}
                      disabled
                    />
                    <label className="pos-fleet-wizard-field">
                      <span>Gate label (required)</span>
                      <input
                        type="text"
                        maxLength={40}
                        required
                        placeholder="e.g. Gate 3 — Screen 2"
                        value={form.desiredConfig.gateLabel || ""}
                        onChange={(event) => patchConfig("gateLabel", event.target.value)}
                      />
                      <small>
                        Stamped on every scan. Naming the door here is what makes
                        the scan log groupable by entrance — and it is why the
                        usher is not asked to invent one at the gate.
                      </small>
                      {gateLabelMissing && (
                        <small style={{ color: "#b3261e", fontWeight: 650 }}>
                          Required for an event terminal.
                        </small>
                      )}
                    </label>
                  </>
                )}
              </div>
            </section>
          )}

          {step === 3 && vendor && (
            <section className="pos-fleet-form-section">
              <label className="pos-fleet-wizard-field">
                <span>Terminal name</span>
                <input
                  autoFocus
                  maxLength={80}
                  value={form.label}
                  placeholder={`${vendor.label} — Till 1`}
                  onChange={(event) =>
                    setForm((c) => ({ ...c, label: event.target.value }))
                  }
                />
                <small>
                  What ops will see on the fleet board. The device's own model
                  name is used if you leave this blank, which is how you end up
                  with four terminals called "SM-T290".
                </small>
              </label>
              <div className="pos-fleet-field-grid with-top-gap">
                <label>
                  <span>Code valid for</span>
                  <AppSelect
                    value={form.ttlMinutes}
                    onChange={(event) =>
                      setForm((c) => ({ ...c, ttlMinutes: Number(event.target.value) }))
                    }
                  >
                    {CODE_TTL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </label>
                <label>
                  <span>Number of terminals</span>
                  <AppSelect
                    value={form.maxUses}
                    onChange={(event) =>
                      setForm((c) => ({ ...c, maxUses: Number(event.target.value) }))
                    }
                  >
                    {[1, 2, 3, 4, 5, 10].map((count) => (
                      <option key={count} value={count}>
                        {count === 1 ? "1 (single use)" : `${count} devices`}
                      </option>
                    ))}
                  </AppSelect>
                </label>
              </div>
              <label className="pos-fleet-wizard-field with-top-gap">
                <span>Note (optional)</span>
                <input
                  maxLength={200}
                  value={form.note}
                  placeholder="e.g. handed to Brian for the Ntinda rollout"
                  onChange={(event) =>
                    setForm((c) => ({ ...c, note: event.target.value }))
                  }
                />
              </label>
              {form.maxUses > 1 && (
                <p className="pos-fleet-wizard-hint">
                  A multi-use code provisions {form.maxUses} terminals with the
                  same assignment and the same name. Rename them individually on
                  the board once they check in.
                </p>
              )}
            </section>
          )}

          {step === 4 && minted && (
            <section className="pos-fleet-form-section">
              <CodeDisplay code={minted.code} tone="hero" />
              <dl className="pos-fleet-diagnostics with-top-gap">
                <div>
                  <dt>Vendor</dt>
                  <dd>{minted.vendor?.name || "—"}</dd>
                </div>
                <div>
                  <dt>Profile</dt>
                  <dd>{profileLabel(minted.terminalProfile)}</dd>
                </div>
                <div>
                  <dt>Market</dt>
                  <dd>{minted.country}</dd>
                </div>
                <div>
                  <dt>Expires</dt>
                  <dd>
                    {fullDate(minted.expiresAt)}
                    {" · "}
                    {timeUntil(minted.expiresAt)}
                  </dd>
                </div>
                <div>
                  <dt>Uses</dt>
                  <dd>
                    {minted.usedCount}/{minted.maxUses}
                  </dd>
                </div>
              </dl>
              <div className="pos-fleet-guide-callout is-info with-top-gap">
                <strong>The code is readable until it expires</strong>
                <span>
                  Re-open it from the Enrollment codes tab to read it back to a
                  field agent. Revoke it there the moment it is no longer needed.
                </span>
              </div>

              <h4 className="pos-fleet-wizard-subhead">Staging script</h4>
              <p className="pos-fleet-wizard-hint">
                Run this on the staging computer with the terminal attached over
                USB. It refuses to continue if the device still has an account,
                which is the one precondition Device Owner cannot recover from.
              </p>
              <CopyableCommand
                command={buildProvisionScript({
                  code: minted.code,
                  label: minted.label,
                  country: minted.country,
                  expiresAt: minted.expiresAt,
                })}
                label="Copy provisioning script"
              />
            </section>
          )}
        </div>

        <div className="pos-fleet-inspector-footer">
          {step === 4 ? (
            <>
              <span>Waiting for the terminal to check in…</span>
              <button type="button" onClick={onClose}>
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="pos-fleet-ghost"
                onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
              >
                {step === 1 ? "Cancel" : "Back"}
              </button>
              {step === 3 ? (
                <button type="button" onClick={mint} disabled={minting}>
                  {minting ? "Creating…" : "Create enrollment code"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={
                    !vendor ||
                    (step === 2 && (!form.terminalProfile || gateLabelMissing))
                  }
                >
                  Continue
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

const CODE_STATUS_TONE = {
  ACTIVE: "is-online",
  REDEEMED: "is-neutral",
  EXPIRED: "is-neutral",
  REVOKED: "is-warning",
  LOCKED: "is-warning",
};

const EnrollmentCodesPanel = ({
  codes,
  loading,
  onRefresh,
  onRevoke,
  onProvision,
}) => (
  <section className="pos-fleet-panel">
    <div className="pos-fleet-panel-head">
      <div>
        <h2>Enrollment codes</h2>
        <span>
          {codes.filter((code) => code.status === "ACTIVE").length} outstanding
        </span>
      </div>
      <div className="pos-fleet-filters">
        <button type="button" className="pos-fleet-clear" onClick={onRefresh}>
          Refresh
        </button>
        <button type="button" className="pos-fleet-primary" onClick={onProvision}>
          Provision terminal
        </button>
      </div>
    </div>

    {loading ? (
      <div className="pos-fleet-empty">
        <Spinner animation="border" size="sm" />
        <span>Loading enrollment codes…</span>
      </div>
    ) : codes.length === 0 ? (
      <div className="pos-fleet-empty">
        <div className="pos-fleet-empty-icon">⌗</div>
        <strong>No enrollment codes yet</strong>
        <span>
          Provision a terminal to mint one. The operator types the code on the
          device instead of a vendor password.
        </span>
      </div>
    ) : (
      <div className="pos-fleet-table-scroll">
        <table className="pos-fleet-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Terminal</th>
              <th>Vendor</th>
              <th>Expires</th>
              <th>Uses</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.id}>
                <td>
                  <div className="pos-fleet-device-cell">
                    <div>
                      <strong className="pos-fleet-code-inline">{code.code}</strong>
                      <span
                        className={`pos-fleet-pill ${
                          CODE_STATUS_TONE[code.status] || "is-neutral"
                        }`}
                      >
                        {profileLabel(code.status)}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <strong className="pos-fleet-cell-title">
                    {code.label || "Unnamed"}
                  </strong>
                  <span className="pos-fleet-cell-subtitle">
                    {profileLabel(code.terminalProfile)} · {code.country}
                  </span>
                </td>
                <td>
                  <strong className="pos-fleet-cell-title">
                    {code.vendor?.name || "—"}
                  </strong>
                  <span className="pos-fleet-cell-subtitle">
                    {vendorTypeLabel(code.vendor?.type)}
                  </span>
                </td>
                <td>
                  <strong className="pos-fleet-cell-title">
                    {code.status === "ACTIVE"
                      ? timeUntil(code.expiresAt)
                      : profileLabel(code.status)}
                  </strong>
                  <span className="pos-fleet-cell-subtitle">
                    {fullDate(code.expiresAt)}
                  </span>
                </td>
                <td>
                  <strong className="pos-fleet-cell-title">
                    {code.usedCount}/{code.maxUses}
                  </strong>
                  <span className="pos-fleet-cell-subtitle">
                    {code.redeemedTerminalIds?.length
                      ? code.redeemedTerminalIds.join(", ")
                      : "Not redeemed"}
                  </span>
                </td>
                <td>
                  {code.status === "ACTIVE" && (
                    <button
                      type="button"
                      className="pos-fleet-clear"
                      onClick={() => onRevoke(code)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const ProvisioningStep = ({ number, title, children, command, copyLabel }) => (
  <article className="pos-fleet-guide-step">
    <div className="pos-fleet-guide-step-number">{number}</div>
    <div className="pos-fleet-guide-step-body">
      <h3>{title}</h3>
      <div className="pos-fleet-guide-step-copy">{children}</div>
      {command && <CopyableCommand command={command} label={copyLabel} />}
    </div>
  </article>
);

const FactoryProvisioningGuide = ({ latestReleaseLabel, onProvision, codesEnabled, fleetReachable }) => (
  <section className="pos-fleet-guide" aria-labelledby="factory-provisioning-title">
    <div className="pos-fleet-guide-intro">
      <div>
        <span className="pos-fleet-eyebrow">Staging runbook</span>
        <h2 id="factory-provisioning-title">Factory provisioning</h2>
        <p>
          Use USB once to install the first signed release and establish Android
          Device Owner. After enrollment, POS Fleet manages the terminal over
          the internet—USB is not the dashboard transport.
        </p>
      </div>
      <div className="pos-fleet-guide-release">
        <span>APK currently published</span>
        <strong>{latestReleaseLabel}</strong>
        <small>Always stage the signed production artifact.</small>
      </div>
    </div>

    {codesEnabled ? (
      <div className="pos-fleet-guide-callout is-info">
        <strong>Start from Provision terminal, not from this page</strong>
        <span>
          The wizard mints an enrollment code carrying the vendor, profile,
          outlet, name and configuration, and generates the staging script
          below with all of it filled in. The operator types the code on the
          device — no vendor password is entered on hardware that leaves the
          office, and the terminal arrives on the board already assigned.
          {" "}
          <button type="button" className="pos-fleet-inline-link" onClick={onProvision}>
            Provision a terminal
          </button>
        </span>
      </div>
    ) : fleetReachable ? (
      <div className="pos-fleet-guide-callout is-warning">
        <strong>Enrollment codes are switched off in this environment</strong>
        <span>
          Until <code>POS_ENROLLMENT_CODES_ENABLED</code> is set, a new terminal
          must sign in once with a real vendor account and appears here after its
          first enrollment heartbeat. The steps below are that fallback path.
        </span>
      </div>
    ) : (
      /* "Switched off" is a claim about configuration. When the fleet request
         itself failed we do not know the flag's value — and asserting it while
         the terminal count reads 0 and the release reads "Not published" tells
         a confident story about a server that is simply down. */
      <div className="pos-fleet-guide-callout is-warning">
        <strong>Cannot reach the Lusuku API</strong>
        <span>
          The terminal count, published release and enrollment-code state on
          this page are unknown, not zero. Check the backend is running, then
          reload.
        </span>
      </div>
    )}

    <div className="pos-fleet-guide-layout">
      <div className="pos-fleet-guide-steps">
        <ProvisioningStep
          number="01"
          title="Enable USB debugging and attach the terminal"
          command={FACTORY_COMMANDS.verify}
          copyLabel="Copy device verification commands"
        >
          <p>
            On the terminal: <strong>Settings → About device →</strong> tap{" "}
            <strong>Build number</strong> seven times, then{" "}
            <strong>Developer options → USB debugging</strong> on. Plug in, pull
            down the notification shade, set the USB mode to{" "}
            <strong>File Transfer</strong> (not Charging), and accept the RSA
            prompt on the terminal&apos;s screen.
          </p>
          <p>
            The device must show as <code>device</code> and the account count
            must be <code>0</code>. Device Owner cannot be set otherwise.
          </p>
        </ProvisioningStep>

        <ProvisioningStep
          number="02"
          title="Run one script"
          command={FACTORY_COMMANDS.script}
          copyLabel="Copy the provisioning command"
        >
          <p>
            This is the whole of provisioning on a clean device. The script
            asserts the account count, downloads the published APK, installs it,
            sets Device Owner — insisting on the literal{" "}
            <code>Success: Device owner set</code> — and launches the app.
          </p>
          <p>
            With several terminals attached, prefix it with{" "}
            <code>ADB_SERIAL=&lt;serial&gt;</code>. The individual commands are
            below if you ever need to run a step by hand.
          </p>
        </ProvisioningStep>

        <ProvisioningStep number="03" title="Only if the script refuses">
          <p>
            <strong>&quot;Accounts present&quot;</strong> — factory-reset from{" "}
            <strong>Settings → System → Reset options → Erase all data</strong>,
            and do not sign in to anything afterwards. Do not hunt for a button
            combo; it varies between batches. A reset clears Developer options,
            so step 01 has to be repeated.
          </p>
          <p>
            <strong>&quot;Already provisioned&quot;</strong> — expected on POS ROMs
            that ship no setup wizard, where the flag is set at boot and never
            clears. Clear the two setup flags, then run the script again:
          </p>
          <CopyableCommand
            command={FACTORY_COMMANDS.clearProvisioned}
            label="Copy setup-flag reset commands"
          />
          <p>
            A factory reset also changes <code>ANDROID_ID</code>, which is how
            terminals are keyed, so the device comes back as a NEW row and the
            old one becomes an orphan. Retire the old row: open it, then{" "}
            <strong>Identity &amp; lifecycle</strong> and set{" "}
            <strong>Lifecycle</strong> to <strong>Retired</strong>.
          </p>
          <p>
            There is no delete, deliberately. A terminal row owns that
            device&apos;s sales, shift and audit history, and hard-deleting it
            would orphan those records instead. Retiring drops the row out of the
            Updates and Online counts and quarantines any later heartbeat, so a
            tablet that comes back to life cannot quietly rejoin the board. Use{" "}
            <strong>Suspended</strong> instead when a device is only temporarily
            out of service.
          </p>
        </ProvisioningStep>

        <ProvisioningStep
          number="04"
          title="Set the clock before the cable comes out"
          command={FACTORY_COMMANDS.autoTime}
          copyLabel="Copy automatic-time commands"
        >
          <p>
            A terminal with no SIM often boots with a wrong clock, and every TLS
            certificate then fails validation — the app reports a secure
            connection it cannot verify and no amount of retrying helps. Once
            the kiosk is locked, Settings is out of reach, so do this now.
          </p>
        </ProvisioningStep>

        <ProvisioningStep
          number="05"
          title="Check what the terminal resolves the API to"
          command={FACTORY_COMMANDS.dnsCheck}
          copyLabel="Copy DNS verification commands"
        >
          <p>
            Both lines must print the same address. The terminal resolves through
            whatever DNS its Wi-Fi advertises, and a router handing out
            third-party resolvers can answer with a host that is not Heroku at
            all. TLS then fails correctly, and the app reports a secure
            connection it cannot verify.
          </p>
          <p>
            A laptop working on the same site proves nothing: it resolves through
            different servers. Desktop works and terminal does not is a DNS
            signal, not an app signal. If the addresses differ, put the terminal
            on Private DNS over TLS and re-check.
          </p>
          <CopyableCommand
            command={FACTORY_COMMANDS.dnsFix}
            label="Copy the DNS fix command"
          />
          <p>
            Never answer this symptom by loosening TLS in the app. The handshake
            failure is the terminal refusing a host that cannot prove its
            identity, which is exactly what it should do on a customer network.
          </p>
          <p>
            If sign-in still fails after the addresses match, stop guessing and
            collect everything in one pass. This reports clock skew, captive
            portal state, the ROM&apos;s CA store, the chain as actually served,
            the installed version and a logcat capture:
          </p>
          <CopyableCommand
            command={FACTORY_COMMANDS.diagnose}
            label="Copy the diagnostic command"
          />
        </ProvisioningStep>

        <ProvisioningStep number="06" title="Sign in and confirm in POS Fleet">
          <p>
            On <strong>Set Up Terminal</strong>: enter the enrollment code from
            the Provision wizard, or sign in with the vendor account where codes
            are switched off. Enrolment assigns the terminal to that vendor and
            applies the profile default automatically — there is nothing to
            assign afterwards.
          </p>
          <p>
            Codes are TYPED. QR provisioning is not available on POS hardware
            that ships without a setup wizard.
          </p>
          <p>
            <strong>Brand terminals need no extra configuration.</strong>
            Enrolment reads <code>operationMode</code> from the account that
            signs in and applies that vendor type&apos;s default profile, so a
            brand owner lands on <code>BRAND_CHECKOUT</code> by itself. A correct
            brand terminal reads <strong>Assignment</strong> = the brand and{" "}
            <strong>Profile</strong> = Brand Checkout with nothing set by hand.
          </p>
          <p>
            Sign in with the account that should OWN this terminal. Enrolment
            binds the device to whoever signs in first, and a later attempt by a
            different vendor is refused with{" "}
            <code>POS_DEVICE_ALREADY_ASSIGNED</code> until a Lusuku super
            administrator reassigns it. On a device you are handing to a
            customer, that is a decision you get to make once.
          </p>
          <p>
            Then on the Terminals tab: check the model and serial against the
            device in front of you, rename it to something a human recognises,
            and wait for <strong>Config applied</strong>. A config version alone
            is not an acknowledgement.
          </p>
        </ProvisioningStep>

        <ProvisioningStep number="07" title="Set a real PIN, then seal the device">
          <ul>
            <li>
              <strong>Cashier PINs</strong> — every cashier without one falls
              back to a shared <code>0000</code> placeholder. It must not
              survive the handover.
            </li>
            <li>Reboot returns directly to Lusuku POS and kiosk controls hold.</li>
            <li>Test printer succeeds and automatic receipt behavior is correct.</li>
            <li>A sale completes with the network off, and syncs when it returns.</li>
            <li>Display-off for ten minutes still advances the fleet heartbeat.</li>
            <li>
              Confirm you know the kiosk support PIN — it is the only way out of
              the kiosk on site.
            </li>
            <li>Disable USB debugging after acceptance and disconnect the cable.</li>
          </ul>
        </ProvisioningStep>
      </div>

      <aside className="pos-fleet-release-guide">
        <span className="pos-fleet-eyebrow">Send a POS update</span>
        <h2>Merge is the trigger—not the whole preparation</h2>
        <p>
          A merge into POS <code>main</code> is sufficient only after the release
          workflow is committed and the protected <code>pos-production</code>
          environment contains every signing and deployment secret.
        </p>

        <ol>
          <li>Deploy compatible Dashboard backend/UI source first.</li>
          <li>Review, commit and push all intended POS changes.</li>
          <li>Open a PR from <code>pos_development</code> to <code>main</code>.</li>
          <li>Merge after CI passes.</li>
          <li>Watch <strong>Actions → Release POS OTA</strong> through production verification.</li>
          <li>Confirm the live version code increased, then watch one pilot terminal update.</li>
        </ol>

        <details>
          <summary>Required GitHub environment secrets</summary>
          <ul>
            <li><code>POS_RELEASE_KEYSTORE_BASE64</code></li>
            <li><code>POS_RELEASE_STORE_PASSWORD</code></li>
            <li><code>POS_RELEASE_KEY_ALIAS</code></li>
            <li><code>POS_RELEASE_KEY_PASSWORD</code></li>
            <li><code>POS_KIOSK_SUPPORT_PIN</code></li>
            <li><code>DASHBOARD_DEPLOY_TOKEN</code></li>
          </ul>
          <p className="pos-fleet-guide-note">
            There is no Heroku credential in this pipeline.{" "}
            <code>HEROKU_API_KEY</code> was removed on 2026-08-31 after an
            expired copy blocked four consecutive releases while the deploy path
            itself was healthy. Heroku deploys itself from Dashboard{" "}
            <code>main</code>.
          </p>
        </details>

        <h3>Verify after the workflow succeeds</h3>
        <CopyableCommand
          command={FACTORY_COMMANDS.verifyRelease}
          label="Copy production verification commands"
        />

        <div className="pos-fleet-guide-callout is-info">
          <strong>Do not publish the same build number</strong>
          <span>
            Android terminals ignore an APK whose version code is not greater
            than the installed release. Let the workflow or recovery script
            produce the next monotonic version.
          </span>
        </div>
      </aside>
    </div>
  </section>
);

const StatusPill = ({ terminal }) => {
  if (terminal.lifecycleStatus === "RETIRED") {
    return <span className="pos-fleet-pill is-neutral">Retired</span>;
  }
  if (terminal.lifecycleStatus === "SUSPENDED") {
    return <span className="pos-fleet-pill is-warning">Suspended</span>;
  }
  return (
    <span className={`pos-fleet-pill ${terminal.online ? "is-online" : "is-offline"}`}>
      <span className="pos-fleet-status-dot" />
      {terminal.online ? "Online" : "Offline"}
    </span>
  );
};

const DeviceTable = ({ terminals, selectedId, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="pos-fleet-empty">
        <Spinner animation="border" size="sm" />
        <span>Loading terminal fleet…</span>
      </div>
    );
  }
  if (!terminals.length) {
    return (
      <div className="pos-fleet-empty">
        <div className="pos-fleet-empty-icon">▣</div>
        <strong>No terminals match these filters</strong>
        <span>New terminals appear here after their first enrollment heartbeat.</span>
      </div>
    );
  }

  return (
    <div className="pos-fleet-table-scroll">
      <table className="pos-fleet-table">
        <thead>
          <tr>
            <th>Terminal</th>
            <th>Assignment</th>
            <th>Profile</th>
            <th>Software</th>
            <th>Last contact</th>
            <th aria-label="Attention" />
          </tr>
        </thead>
        <tbody>
          {terminals.map((terminal) => (
            <tr
              key={terminal.terminalId}
              className={selectedId === terminal.terminalId ? "is-selected" : ""}
              onClick={() => onSelect(terminal.terminalId)}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  onSelect(terminal.terminalId);
                }
              }}
            >
              <td>
                <div className="pos-fleet-device-cell">
                  <div className="pos-fleet-device-icon">POS</div>
                  <div>
                    <strong>{terminal.label}</strong>
                    <span>{terminal.terminalId}</span>
                    <StatusPill terminal={terminal} />
                  </div>
                </div>
              </td>
              <td>
                <strong className="pos-fleet-cell-title">
                  {terminal.assignment?.vendorName || "Not assigned"}
                </strong>
                <span className="pos-fleet-cell-subtitle">
                  {vendorTypeLabel(terminal.assignment?.vendorType)}
                </span>
              </td>
              <td>
                <span className="pos-fleet-profile-badge">
                  {profileLabel(terminal.assignment?.terminalProfile) || "—"}
                </span>
              </td>
              <td>
                <strong className="pos-fleet-cell-title">
                  {terminal.reportedState?.appVersionName || "Unknown"}
                </strong>
                <span className="pos-fleet-cell-subtitle">
                  Build {terminal.reportedState?.appVersionCode || "—"}
                  {terminal.release?.updateAvailable ? " · update ready" : ""}
                </span>
              </td>
              <td>
                <strong className="pos-fleet-cell-title">
                  {relativeTime(terminal.lastSeenAt)}
                </strong>
                <span className="pos-fleet-cell-subtitle">
                  {terminal.reportedState?.networkType || "Network unknown"}
                </span>
              </td>
              <td>
                {terminal.attentionReasons?.length > 0 && (
                  <span
                    className="pos-fleet-attention"
                    title={terminal.attentionReasons.join(", ")}
                  >
                    {terminal.attentionReasons.length}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PosTerminalFleetView = () => {
  const { activeCountry } = useCountry();
  const confirmDialog = useConfirmDialog();
  const [activeSection, setActiveSection] = useState("terminals");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [terminals, setTerminals] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    online: 0,
    offline: 0,
    suspended: 0,
    // Retired terminals are counted separately and excluded from total/offline.
    // They used to be folded into both, so every disposed tablet read as a
    // permanent outage on a board ops checks every morning.
    retired: 0,
    unassigned: 0,
    updateNeeded: 0,
  });
  const [vendorTypes, setVendorTypes] = useState([]);
  const [release, setRelease] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearching, setVendorSearching] = useState(false);
  const [pickedVendor, setPickedVendor] = useState(null);
  const [vendorOutlets, setVendorOutlets] = useState([]);
  const [vendorOutletsLoading, setVendorOutletsLoading] = useState(false);
  const [commandBusy, setCommandBusy] = useState("");
  const [liveStatus, setLiveStatus] = useState("connecting");
  const [commandCatalog, setCommandCatalog] = useState([]);
  const [serviceStateMinBuild, setServiceStateMinBuild] = useState(0);
  const [codesEnabled, setCodesEnabled] = useState(false);
  const [fleetReachable, setFleetReachable] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  // The rotated `posd_` plaintext, held in component state ONLY. It is never
  // persisted, never re-fetchable, and is dropped the moment the drawer closes
  // — exactly as the endpoint intends.
  const [revealedToken, setRevealedToken] = useState(null);
  const [credentialBusy, setCredentialBusy] = useState("");
  const searchTimer = useRef(null);
  const vendorTimer = useRef(null);
  const liveRefreshTimer = useRef(null);
  const loadFleetRef = useRef(null);
  const selectedIdRef = useRef(null);
  const filtersRef = useRef(EMPTY_FILTERS);

  const loadFleet = useCallback(async (
    nextFilters = filters,
    { silent = false } = {}
  ) => {
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const params = Object.fromEntries(
        Object.entries(nextFilters).filter(([, value]) => value)
      );
      const { data } = await apiPosListTerminals(params);
      setTerminals(data.terminals || []);
      setSummary(data.summary || {});
      setVendorTypes(data.vendorTypes || []);
      setCommandCatalog(data.commands || []);
      setServiceStateMinBuild(Number(data.serviceStateMinAppVersionCode || 0));
      setCodesEnabled(data.enrollmentCodesEnabled === true);
      setFleetReachable(true);
      setRelease(data.release || {});
      if (
        selectedId &&
        !(data.terminals || []).some((item) => item.terminalId === selectedId)
      ) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (error) {
      setFleetReachable(false);
      setLoadError(errorMessage(error, "Could not load the POS terminal fleet."));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, selectedId]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadFleet(filters), 280);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [filters]);

  const loadDetail = useCallback(async (terminalId) => {
    setSelectedId(terminalId);
    setDetailLoading(true);
    setFeedback(null);
    setRevealedToken(null);
    setPickedVendor(null);
    setVendorQuery("");
    setVendorResults([]);
    setVendorOutlets([]);
    try {
      const { data } = await apiPosGetTerminal(terminalId);
      const terminal = data.terminal;
      setDetail(data);
      setDraft({
        label: terminal.label || "",
        lifecycleStatus: terminal.lifecycleStatus || "ACTIVE",
        assignment: {
          vendorUserId: terminal.assignment?.vendorUserId || "",
          vendorName: terminal.assignment?.vendorName || "",
          vendorType: terminal.assignment?.vendorType || "",
          terminalProfile: terminal.assignment?.terminalProfile || "",
          outletId: terminal.assignment?.outletId || "",
          eventOrganizerId: terminal.assignment?.eventOrganizerId || "",
        },
        desiredConfig: {
          autoPrintReceipts:
            terminal.desiredConfig?.autoPrintReceipts !== false,
          kioskLocked: terminal.desiredConfig?.kioskLocked !== false,
          allowOrderFulfillment:
            terminal.desiredConfig?.allowOrderFulfillment === true,
          allowTicketScanning:
            terminal.desiredConfig?.allowTicketScanning === true,
          allowTicketSales: terminal.desiredConfig?.allowTicketSales === true,
          gateLabel: terminal.desiredConfig?.gateLabel || "",
          serviceState:
            terminal.desiredConfig?.serviceState === "OUT_OF_SERVICE"
              ? "OUT_OF_SERVICE"
              : "IN_SERVICE",
          outOfServiceMessage: terminal.desiredConfig?.outOfServiceMessage || "",
          heartbeatIntervalSeconds:
            terminal.desiredConfig?.heartbeatIntervalSeconds || 60,
          updateChannel:
            terminal.desiredConfig?.updateChannel || "PRODUCTION",
        },
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: errorMessage(error, "Could not load terminal details."),
      });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFleetRef.current = loadFleet;
    selectedIdRef.current = selectedId;
    filtersRef.current = filters;
  }, [filters, loadFleet, selectedId]);

  useEffect(() => {
    const token = getTokenFromLocalStorage();
    const fleetSocket = io(dashboardApiBaseUrl(), {
      auth: token ? { token } : undefined,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
      timeout: 20_000,
    });

    const refreshFromSignal = (event = {}) => {
      if (liveRefreshTimer.current) clearTimeout(liveRefreshTimer.current);
      liveRefreshTimer.current = setTimeout(async () => {
        await loadFleetRef.current?.(filtersRef.current, { silent: true });
        const currentId = selectedIdRef.current;
        if (currentId && (!event.terminalId || event.terminalId === currentId)) {
          try {
            const { data } = await apiPosGetTerminal(currentId);
            // Preserve the operator's in-progress form draft while refreshing
            // health, acknowledgements and audit history in the inspector.
            setDetail(data);
          } catch (_error) {
            // The REST fallback will converge on the next visible-page poll.
          }
        }
      }, 250);
    };

    fleetSocket.on("connect", () => {
      fleetSocket.emit(
        "joinPosFleetRoom",
        { country: activeCountry },
        (ack = {}) => {
          setLiveStatus(ack.ok ? "live" : "unauthorized");
          if (ack.ok) refreshFromSignal();
        }
      );
    });
    fleetSocket.on("disconnect", () => setLiveStatus("reconnecting"));
    fleetSocket.on("connect_error", () => setLiveStatus("reconnecting"));
    fleetSocket.on("pos:fleet_changed", refreshFromSignal);

    // Socket.IO is the low-latency rail; this quiet poll is the convergence
    // rail for a missed event and for time-derived offline boundaries.
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") refreshFromSignal();
    }, 30_000);

    return () => {
      clearInterval(poll);
      if (liveRefreshTimer.current) clearTimeout(liveRefreshTimer.current);
      fleetSocket.off("pos:fleet_changed", refreshFromSignal);
      fleetSocket.disconnect();
    };
  }, [activeCountry]);

  useEffect(() => {
    if (vendorTimer.current) clearTimeout(vendorTimer.current);
    const query = vendorQuery.trim();
    if (query.length < 2) {
      setVendorResults([]);
      setVendorSearching(false);
      return undefined;
    }
    vendorTimer.current = setTimeout(async () => {
      setVendorSearching(true);
      try {
        const { data } = await apiEntitySearch("VENDOR", query, 12);
        const supported = new Set(vendorTypes.map((item) => item.value));
        setVendorResults(
          (data.results || []).filter((item) =>
            supported.has(item.operationMode)
          )
        );
      } catch (_error) {
        setVendorResults([]);
      } finally {
        setVendorSearching(false);
      }
    }, 280);
    return () => vendorTimer.current && clearTimeout(vendorTimer.current);
  }, [vendorQuery, vendorTypes]);

  useEffect(() => {
    const vendorUserId = draft?.assignment?.vendorUserId;
    const vendorType =
      pickedVendor?.operationMode || draft?.assignment?.vendorType;
    // Outlet scoping is no longer restaurant-only (R1/H3): a three-branch
    // pharmacy or supermarket needs it for exactly the same reason. The server
    // answers with an empty list for a vendor that has no outlet collection, so
    // asking is harmless — only event organizers are skipped, because a gate is
    // not an outlet.
    if (!vendorUserId || !vendorType || vendorType === "EVENT_ORGANIZER") {
      setVendorOutlets([]);
      setVendorOutletsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setVendorOutletsLoading(true);
    apiPosGetVendorOutlets(vendorUserId)
      .then(({ data }) => {
        if (!cancelled) setVendorOutlets(data.outlets || []);
      })
      .catch(() => {
        if (!cancelled) setVendorOutlets([]);
      })
      .finally(() => {
        if (!cancelled) setVendorOutletsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    draft?.assignment?.vendorUserId,
    draft?.assignment?.vendorType,
    pickedVendor?.operationMode,
  ]);

  const selectedVendorType =
    pickedVendor?.operationMode || draft?.assignment?.vendorType || "";
  const selectedTypeMetadata = vendorTypes.find(
    (item) => item.value === selectedVendorType
  );
  const availableProfiles = selectedTypeMetadata?.profiles || [];

  const patchDraft = (path, value) => {
    setDraft((current) => {
      if (!current) return current;
      if (path[0] === "assignment") {
        return {
          ...current,
          assignment: { ...current.assignment, [path[1]]: value },
        };
      }
      if (path[0] === "desiredConfig") {
        return {
          ...current,
          desiredConfig: { ...current.desiredConfig, [path[1]]: value },
        };
      }
      return { ...current, [path[0]]: value };
    });
  };

  const chooseVendor = (vendor) => {
    setPickedVendor(vendor);
    const typeMeta = vendorTypes.find(
      (item) => item.value === vendor.operationMode
    );
    setDraft((current) => ({
      ...current,
      assignment: {
        ...current.assignment,
        vendorUserId: String(vendor.id),
        vendorName: vendor.label,
        vendorType: vendor.operationMode,
        terminalProfile: typeMeta?.defaultProfile || "",
        outletId: "",
        eventOrganizerId: vendor.eventOrganizerId
          ? String(vendor.eventOrganizerId)
          : "",
      },
      desiredConfig: {
        ...current.desiredConfig,
        ...(typeMeta?.defaultConfig || {}),
      },
    }));
    setVendorQuery("");
    setVendorResults([]);
  };

  /**
   * `overrideOpenShift` re-sends a save the server refused with
   * POS_STALE_SHIFT_BLOCKS_ASSIGNMENT: the terminal last reported an open
   * shift and has been offline since, so nobody can close it and the console
   * used to refuse forever (Sep 12 2026). It is only ever set by the button in
   * that refusal, never by the plain Save, and the server ignores it for a
   * terminal that is actually online — that one has a real remedy.
   */
  const saveTerminal = async ({
    overrideOpenShift = false,
    draftOverride = null,
  } = {}) => {
    const terminalDraft = draftOverride || draft;
    if (!terminalDraft || !selectedId) return;
    // Refuse locally for the same reason the server refuses (E3): an event
    // terminal with no gate label fills TicketScanLog.gate with whatever each
    // usher typed, and that cannot be reconciled after the night is over.
    if (
      terminalDraft.assignment?.terminalProfile === "EVENT_ACCESS" &&
      !String(terminalDraft.desiredConfig?.gateLabel || "").trim()
    ) {
      setFeedback({
        type: "error",
        message:
          "Name the door this terminal stands at. Every scan is stamped with the gate label, and it is the only way the gate log can be reconciled by entrance.",
      });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const assignment = {
        vendorUserId: terminalDraft.assignment.vendorUserId || "",
        terminalProfile: terminalDraft.assignment.terminalProfile || undefined,
        outletId: terminalDraft.assignment.outletId || null,
        eventOrganizerId: terminalDraft.assignment.eventOrganizerId || undefined,
        ...(overrideOpenShift ? { overrideOpenShift: true } : {}),
      };
      const originalAssignment = currentTerminal?.assignment || {};
      const assignmentChanged =
        String(assignment.vendorUserId || "") !==
          String(originalAssignment.vendorUserId || "") ||
        String(assignment.terminalProfile || "") !==
          String(originalAssignment.terminalProfile || "") ||
        String(assignment.outletId || "") !==
          String(originalAssignment.outletId || "") ||
        String(assignment.eventOrganizerId || "") !==
          String(originalAssignment.eventOrganizerId || "");
      const payload = {
        label: terminalDraft.label.trim(),
        lifecycleStatus: terminalDraft.lifecycleStatus,
        desiredConfig: terminalDraft.desiredConfig,
        ...(assignmentChanged ? { assignment } : {}),
      };
      const { data } = await apiPosUpdateTerminal(selectedId, payload);
      await Promise.all([loadDetail(selectedId), loadFleet(filters)]);
      const override = data?.shiftOverride;
      setFeedback({
        type: "success",
        message: override
          ? `Saved. The shift ${
              override.previousShift?.cashierName
                ? `${override.previousShift.cashierName} left open`
                : "last reported open"
            } was set aside and recorded in the audit trail. ${
              override.closeShiftQueued
                ? "A Close shift command waits for the terminal for seven days, so the drawer is ended before the new assignment applies."
                : "This build cannot take a remote close, so the cashier will be asked to close the shift when the terminal returns; the new assignment applies after that."
            }`
          : "Saved and signalled in real time. The status changes to Config applied only after the terminal reports every effective setting.",
      });
    } catch (error) {
      const refusal = error?.response?.data || {};
      if (refusal.code === "POS_STALE_SHIFT_BLOCKS_ASSIGNMENT") {
        // The facts, then the choice. The cost is stated because it is real:
        // the server binds every device write to the CURRENT assignment, so a
        // sale this terminal took offline for the old vendor and has not yet
        // sent will be refused once it reconnects.
        setFeedback({
          type: "warning",
          message: `${shiftLockSummary(refusal.shift, currentTerminal)} Nobody can close it from here. Override to change the assignment now: any sale it took offline for ${
            currentTerminal?.assignment?.vendorName || "the previous vendor"
          } and has not yet sent will be refused when it reconnects.`,
          // A kind, not a closure: the button dispatches through the CURRENT
          // render's handlers, so an edit made after the refusal is what gets
          // saved, not the draft as it stood when the refusal arrived.
          action: { label: "Override and save", kind: "OVERRIDE_SHIFT" },
        });
        return;
      }
      if (refusal.code === "POS_ACTIVE_SHIFT_BLOCKS_ASSIGNMENT") {
        const closeShift = commandCatalog.find(
          (entry) => entry.type === "CLOSE_SHIFT"
        );
        const blocked = closeShift
          ? commandBlockReason(closeShift, currentTerminal)
          : "";
        setFeedback({
          type: "error",
          message: `${shiftLockSummary(refusal.shift, currentTerminal)} The terminal is online, so close the shift first and save again.${
            blocked ? ` Remote close is unavailable: ${blocked}.` : ""
          }`,
          action:
            closeShift && !blocked
              ? { label: "Close shift now", kind: "CLOSE_SHIFT" }
              : null,
        });
        return;
      }
      setFeedback({
        type: "error",
        message: errorMessage(error, "Could not save terminal configuration."),
      });
    } finally {
      setSaving(false);
    }
  };

  /** The remedy a refusal carries, run through this render's handlers. */
  const runFeedbackAction = (action) => {
    if (action?.kind === "OVERRIDE_SHIFT") {
      saveTerminal({ overrideOpenShift: true });
    } else if (action?.kind === "CLOSE_SHIFT") {
      sendCommand("CLOSE_SHIFT");
    }
  };

  const unassignTerminal = () => {
    if (!draft || !selectedId) return;
    const previousVendor =
      currentTerminal?.assignment?.vendorName || "the current vendor";
    const nextDraft = {
      ...draft,
      assignment: {
        vendorUserId: "",
        vendorName: "",
        vendorType: "",
        terminalProfile: "",
        outletId: "",
        eventOrganizerId: "",
      },
    };
    confirmDialog.ask({
      title: `Unassign ${currentTerminal?.label || "this terminal"}?`,
      body: `This removes ${previousVendor} immediately. When the terminal reconnects it will stop at Waiting for vendor assignment; it cannot open another shift until reassigned.`,
      tone: "warning",
      confirmLabel: "Unassign terminal",
      busyLabel: "Unassigning…",
      onConfirm: async () => {
        setPickedVendor(null);
        // Keep the draft aligned with the requested operation so the existing
        // stale-shift override action can retry this exact unassignment.
        setDraft(nextDraft);
        await saveTerminal({ draftOverride: nextDraft });
      },
    });
  };

  const sendCommand = async (type) => {
    if (!selectedId) return;
    setCommandBusy(type);
    setFeedback(null);
    try {
      await apiPosQueueTerminalCommand(selectedId, type);
      await loadDetail(selectedId);
      setFeedback({
        type: "success",
        message:
          "Command queued and signalled in real time; heartbeat delivery remains active as a fallback. It expires in 24 hours if the terminal never comes back.",
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: errorMessage(error, "Could not queue the terminal command."),
      });
    } finally {
      setCommandBusy("");
    }
  };

  const cancelCommand = async (commandId) => {
    if (!selectedId) return;
    setCommandBusy(commandId);
    setFeedback(null);
    try {
      const { data } = await apiPosCancelTerminalCommand(selectedId, commandId);
      await loadDetail(selectedId);
      setFeedback({
        // A cancel after delivery is a real race and is reported as a warning,
        // not a success — telling ops a command was stopped when it may already
        // have run is the one thing this control must never do.
        type: data.wasDelivered ? "warning" : "success",
        message: data.message || "Command cancelled.",
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: errorMessage(error, "Could not cancel the command."),
      });
    } finally {
      setCommandBusy("");
    }
  };

  const rotateCredential = (revoke) => {
    if (!selectedId) return;
    const terminalLabel = currentTerminal?.label || selectedId;
    confirmDialog.ask({
      title: revoke
        ? `Revoke the credential for "${terminalLabel}"?`
        : `Rotate the credential for "${terminalLabel}"?`,
      body: revoke
        ? "The terminal stops authenticating immediately and is suspended. It comes back only by re-enrolling, which its own operator can do."
        : "The current token stops working the moment this returns. The replacement is shown ONCE: enter it on the terminal before closing this panel.",
      tone: revoke ? "danger" : "warning",
      confirmLabel: revoke ? "Revoke credential" : "Rotate credential",
      busyLabel: revoke ? "Revoking..." : "Rotating...",
      onConfirm: () => runCredentialRotation(revoke),
    });
  };

  const runCredentialRotation = async (revoke) => {
    setCredentialBusy(revoke ? "revoke" : "rotate");
    setFeedback(null);
    setRevealedToken(null);
    try {
      const { data } = await apiPosRotateTerminalCredential(selectedId, {
        revoke: revoke || undefined,
      });
      if (data.deviceToken) setRevealedToken(data.deviceToken);
      await Promise.all([loadDetail(selectedId), loadFleet(filters)]);
      // loadDetail resets feedback, so set it after.
      setFeedback({ type: revoke ? "warning" : "success", message: data.message });
      if (data.deviceToken) setRevealedToken(data.deviceToken);
    } catch (error) {
      setFeedback({
        type: "error",
        message: errorMessage(error, "Could not rotate the terminal credential."),
      });
    } finally {
      setCredentialBusy("");
    }
  };

  const loadCodes = useCallback(async () => {
    setCodesLoading(true);
    try {
      const { data } = await apiPosListEnrollmentCodes();
      setCodes(data.enrollmentCodes || []);
    } catch (_error) {
      setCodes([]);
    } finally {
      setCodesLoading(false);
    }
  }, []);

  const revokeCode = (code) => {
    confirmDialog.ask({
      title: `Revoke enrolment code ${code.code}?`,
      body: "Any terminal that has not yet used it will be refused.",
      tone: "danger",
      confirmLabel: "Revoke code",
      busyLabel: "Revoking...",
      onConfirm: async () => {
        try {
          await apiPosRevokeEnrollmentCode(code.id);
          await loadCodes();
        } catch (error) {
          setLoadError(errorMessage(error, "Could not revoke that code."));
        }
      },
    });
  };

  // Load once the flag is known, not only when the tab is opened — otherwise
  // the tab's outstanding-codes badge reads 0 until you click it, which is
  // exactly backwards for a number whose job is to make you click it.
  useEffect(() => {
    if (codesEnabled) loadCodes();
  }, [codesEnabled, activeSection, loadCodes]);

  const currentTerminal = detail?.terminal;
  // Same rule as the service commands, and for a far more dangerous reason: a
  // build that does not read serviceState keeps SELLING while the console says
  // it is out of service. Offering the switch would be worse than not having it.
  const reportedBuild = Number(
    currentTerminal?.reportedState?.appVersionCode || 0
  );
  const serviceStateBlockedReason =
    serviceStateMinBuild > 0 &&
    reportedBuild > 0 &&
    reportedBuild < serviceStateMinBuild
      ? `Needs POS build ${serviceStateMinBuild} — this terminal reports ${reportedBuild}, which ignores this setting and would keep selling.`
      : "";
  /* ── E2: only the DEVICE can say ticket selling exists ────────────────────
   * The console used to render this row permanently off with an explanation,
   * which was honest but static; the API accepted the flag anyway. Both sides
   * now read the same fact — the capability list in the terminal's own device
   * report — so the console cannot offer what the server will refuse, and
   * neither can promise a screen the build does not contain. */
  const reportedCapabilities = currentTerminal?.reportedState?.capabilities || [];
  const ticketSalesSupported = reportedCapabilities.includes("TICKET_SALES");

  /* ── E3: a gate with no name cannot be reconciled ─────────────────────────
   * TicketScanLog.gate is stamped from this label. Blank means the usher types
   * their own, so one evening's scans arrive as "Main", "", "screen2" and
   * "Screen 2". The server refuses the save (POS_GATE_LABEL_REQUIRED); the
   * console says so before the operator hits save rather than after. */
  const gateLabelRequired =
    draft?.assignment?.terminalProfile === "EVENT_ACCESS" ||
    (selectedVendorType === "EVENT_ORGANIZER" && !draft?.assignment?.terminalProfile);
  const gateLabelMissing =
    gateLabelRequired && !String(draft?.desiredConfig?.gateLabel || "").trim();

  const profileExplanation = selectedTypeMetadata?.useCase || "";
  /* ── Sep 12 2026: the reason on the control, not in a 409 ─────────────────
   * `assignmentLock` is the server's own answer (assignmentShiftGuard) about
   * whether this terminal's vendor can change right now and what the remedy
   * is: CLOSE_SHIFT while the terminal is online mid-shift, OVERRIDE when it
   * last reported a shift and has been offline since. Before this, the drawer
   * showed nothing until Save, and then only "close the active shift first"
   * for a device nobody could reach. */
  const assignmentLock = currentTerminal?.assignmentLock || null;
  const closeShiftCommand = commandCatalog.find(
    (entry) => entry.type === "CLOSE_SHIFT"
  );
  const closeShiftBlockedReason = closeShiftCommand
    ? commandBlockReason(closeShiftCommand, currentTerminal)
    : "";
  // The card under "Vendor assignment" shows the DRAFT. After a refused save
  // it kept showing the vendor that was picked as if it were the assignment,
  // while the fleet table still showed the real one; say which is which.
  const assignmentDraftDiffers =
    Boolean(draft) &&
    Boolean(currentTerminal) &&
    String(draft.assignment?.vendorUserId || "") !==
      String(currentTerminal.assignment?.vendorUserId || "");
  const latestReleaseLabel = release.versionName
    ? `${release.versionName} (${release.versionCode})`
    : fleetReachable
      ? "Not published"
      : "Unknown";
  const appliedConfig =
    currentTerminal?.configApplied === true;

  const auditRows = useMemo(() => detail?.audit || [], [detail]);

  return (
    <div className="pos-fleet-page">
      <ConfirmDialog {...confirmDialog.props} />
      <section className="pos-fleet-hero">
        <div>
          <span className="pos-fleet-eyebrow">Lusuku operations</span>
          <h1>POS terminal fleet</h1>
          <p>
            Monitor connectivity, assign each device to the right vendor
            workflow, and manage configuration without touching the terminal.
          </p>
        </div>
        <div className="pos-fleet-release">
          <span>Production release</span>
          <strong>{latestReleaseLabel}</strong>
          <small className={`pos-fleet-live is-${liveStatus}`}>
            <i />
            {liveStatus === "live"
              ? "Fleet live"
              : liveStatus === "unauthorized"
                ? "Live access denied"
                : "Realtime reconnecting"}
          </small>
          {activeSection === "terminals" ? (
            <button type="button" onClick={() => loadFleet(filters)} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh fleet"}
            </button>
          ) : (
            <button type="button" onClick={() => setActiveSection("terminals")}>
              View terminals
            </button>
          )}
        </div>
      </section>

      <div className="pos-fleet-tabs" role="tablist" aria-label="POS fleet sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "terminals"}
          className={activeSection === "terminals" ? "is-active" : ""}
          onClick={() => setActiveSection("terminals")}
        >
          <span className="pos-fleet-tab-icon" aria-hidden="true">▣</span>
          <span>
            <strong>Terminals</strong>
            <small>Monitor and configure</small>
          </span>
          <i>{summary.total || 0}</i>
        </button>
        {codesEnabled && (
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === "codes"}
            className={activeSection === "codes" ? "is-active" : ""}
            onClick={() => {
              setActiveSection("codes");
              setSelectedId(null);
              setDetail(null);
            }}
          >
            <span className="pos-fleet-tab-icon" aria-hidden="true">⌗</span>
            <span>
              <strong>Enrollment codes</strong>
              <small>Provision without a password</small>
            </span>
            <i>{codes.filter((code) => code.status === "ACTIVE").length || 0}</i>
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "provisioning"}
          className={activeSection === "provisioning" ? "is-active" : ""}
          onClick={() => {
            setActiveSection("provisioning");
            setSelectedId(null);
            setDetail(null);
          }}
        >
          <span className="pos-fleet-tab-icon" aria-hidden="true">↳</span>
          <span>
            <strong>Factory provisioning</strong>
            <small>Setup and OTA guide</small>
          </span>
          <i>Guide</i>
        </button>
      </div>

      {activeSection === "terminals" ? (
        <>
          {/*
            Five tiles now that retired stands on its own. The shared stylesheet
            hard-codes four columns; the override lives here rather than in the
            CSS so this view owns its own layout change.
          */}
          <section
            className="pos-fleet-summary-grid"
            style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}
          >
            <SummaryCard
              label="In service"
              value={summary.total || 0}
              tone="blue"
              detail="Enrolled and not retired"
            />
            <SummaryCard
              label="Online"
              value={summary.online || 0}
              tone="green"
              detail={`${summary.offline || 0} currently offline`}
            />
            <SummaryCard
              label="Unassigned"
              value={summary.unassigned || 0}
              tone="amber"
              detail="Waiting for a vendor"
            />
            <SummaryCard
              label="Updates"
              value={summary.updateNeeded || 0}
              tone="violet"
              detail="Behind production release"
            />
            {/*
              Retired is its own tile rather than a share of "offline". A
              returned or written-off tablet is never heard from again by
              design; counting it as an outage put a permanent false alarm on
              the board, which is how a real outage stops being noticed.
            */}
            <SummaryCard
              label="Retired"
              value={summary.retired || 0}
              tone="neutral"
              detail="Withdrawn — not counted as offline"
            />
          </section>

          <section className="pos-fleet-panel">
            <div className="pos-fleet-panel-head">
              <div>
                <h2>Devices</h2>
                <span>{terminals.length} shown</span>
              </div>
              <div className="pos-fleet-filters">
            <label className="pos-fleet-search">
              <span>⌕</span>
              <input
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
                placeholder="Search label, ID, model or serial"
              />
            </label>
            <AppSelect
              value={filters.vendorType}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  vendorType: event.target.value,
                }))
              }
              aria-label="Filter by vendor type"
            >
              <option value="">All vendor types</option>
              {vendorTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </AppSelect>
            <AppSelect
              value={filters.connectivity}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  connectivity: event.target.value,
                }))
              }
              aria-label="Filter by connectivity"
            >
              <option value="">Any connectivity</option>
              <option value="ONLINE">Online</option>
              <option value="OFFLINE">Offline</option>
            </AppSelect>
            <AppSelect
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
              aria-label="Filter by lifecycle status"
            >
              <option value="">Any lifecycle</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="RETIRED">Retired</option>
            </AppSelect>
            {Object.values(filters).some(Boolean) && (
              <button
                type="button"
                className="pos-fleet-clear"
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                Clear
              </button>
            )}
            {codesEnabled && (
              <button
                type="button"
                className="pos-fleet-primary"
                onClick={() => setWizardOpen(true)}
              >
                Provision terminal
              </button>
            )}
              </div>
            </div>
            {loadError && <div className="pos-fleet-alert is-error">{loadError}</div>}
            <DeviceTable
              terminals={terminals}
              selectedId={selectedId}
              onSelect={loadDetail}
              loading={loading}
            />
          </section>
        </>
      ) : activeSection === "codes" ? (
        <EnrollmentCodesPanel
          codes={codes}
          loading={codesLoading}
          onRefresh={loadCodes}
          onRevoke={revokeCode}
          onProvision={() => setWizardOpen(true)}
        />
      ) : (
        <FactoryProvisioningGuide
          latestReleaseLabel={latestReleaseLabel}
          codesEnabled={codesEnabled}
          fleetReachable={fleetReachable}
          onProvision={() => setWizardOpen(true)}
        />
      )}

      {wizardOpen && (
        <ProvisionWizard
          vendorTypes={vendorTypes}
          onClose={() => setWizardOpen(false)}
          onMinted={() => {
            loadCodes();
            loadFleet(filters, { silent: true });
          }}
        />
      )}

      {selectedId && (
        <div
          className="pos-fleet-inspector-backdrop"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) {
              setSelectedId(null);
              setDetail(null);
            }
          }}
        >
          <aside className="pos-fleet-inspector" aria-label="Terminal details">
            <div className="pos-fleet-inspector-head">
              <div>
                <span>Terminal configuration</span>
                <h2>{currentTerminal?.label || selectedId}</h2>
                <small>{selectedId}</small>
              </div>
              <button
                type="button"
                aria-label="Close terminal details"
                onClick={() => {
                  setSelectedId(null);
                  setDetail(null);
                }}
              >
                ×
              </button>
            </div>

            {detailLoading && !detail ? (
              <div className="pos-fleet-inspector-loading">
                <Spinner animation="border" size="sm" />
                Loading terminal…
              </div>
            ) : draft && currentTerminal ? (
              <div className="pos-fleet-inspector-body">
                {feedback && (
                  <div className={`pos-fleet-alert is-${feedback.type}`}>
                    <span>{feedback.message}</span>
                    {feedback.action && (
                      <button
                        type="button"
                        className="pos-fleet-alert-action"
                        onClick={() => runFeedbackAction(feedback.action)}
                        disabled={saving || Boolean(commandBusy)}
                      >
                        {feedback.action.label}
                      </button>
                    )}
                  </div>
                )}

                <div className="pos-fleet-health-strip">
                  <StatusPill terminal={currentTerminal} />
                  <span>Last heartbeat {relativeTime(currentTerminal.lastSeenAt)}</span>
                  <span
                    className={`pos-fleet-config-state ${
                      appliedConfig ? "is-applied" : "is-pending"
                    }`}
                    title={
                      appliedConfig
                        ? "Every desired setting is confirmed by the terminal."
                        : `Waiting for: ${(
                            currentTerminal.configMismatches || [
                              "terminal acknowledgement",
                            ]
                          ).join(", ")}`
                    }
                  >
                    {appliedConfig ? "Config applied" : "Config pending"}
                  </span>
                </div>

                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <h3>Identity & lifecycle</h3>
                  </div>
                  <div className="pos-fleet-field-grid">
                    <label>
                      <span>Terminal label</span>
                      <input
                        value={draft.label}
                        maxLength={80}
                        onChange={(event) =>
                          patchDraft(["label"], event.target.value)
                        }
                      />
                    </label>
                    <label>
                      <span>Lifecycle</span>
                      <AppSelect
                        value={draft.lifecycleStatus}
                        onChange={(event) =>
                          patchDraft(["lifecycleStatus"], event.target.value)
                        }
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="SUSPENDED">Suspended</option>
                        <option value="RETIRED">Retired</option>
                      </AppSelect>
                    </label>
                  </div>
                </section>

                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <div>
                      <h3>Vendor assignment</h3>
                      <p>Changing this switches the terminal’s Lusuku workflow.</p>
                    </div>
                    {draft.assignment.vendorUserId && (
                      <button
                        type="button"
                        onClick={unassignTerminal}
                        disabled={assignmentLock?.remedy === "CLOSE_SHIFT"}
                        title={
                          assignmentLock?.remedy === "CLOSE_SHIFT"
                            ? "Close the open shift first"
                            : undefined
                        }
                      >
                        Unassign now
                      </button>
                    )}
                  </div>

                  {assignmentLock && (
                    <div
                      className={`pos-fleet-guide-callout ${
                        assignmentLock.remedy === "OVERRIDE" ? "is-info" : "is-warning"
                      } pos-fleet-assignment-lock`}
                    >
                      <strong>
                        {assignmentLock.remedy === "OVERRIDE"
                          ? "Shift left open"
                          : "Shift open"}
                      </strong>
                      <span>
                        {shiftLockSummary(assignmentLock.shift, currentTerminal)}{" "}
                        {assignmentLock.remedy === "OVERRIDE"
                          ? "Nobody can close it until the terminal reconnects. You can still unassign or reassign: Save will ask you to confirm the override."
                          : "The terminal is online, so close the shift before changing its vendor."}
                      </span>
                      {assignmentLock.remedy === "CLOSE_SHIFT" && closeShiftCommand && (
                        <button
                          type="button"
                          onClick={() => sendCommand("CLOSE_SHIFT")}
                          disabled={Boolean(commandBusy) || Boolean(closeShiftBlockedReason)}
                          title={closeShiftBlockedReason || closeShiftCommand.hint}
                        >
                          {commandBusy === "CLOSE_SHIFT" ? "Sending…" : "Close shift now"}
                        </button>
                      )}
                    </div>
                  )}

                  {draft.assignment.vendorUserId && (
                    <div
                      className={`pos-fleet-current-assignment${
                        assignmentDraftDiffers ? " is-unsaved" : ""
                      }`}
                    >
                      <span>{vendorTypeLabel(selectedVendorType)}</span>
                      <strong>{draft.assignment.vendorName || "Selected vendor"}</strong>
                      <small>
                        {assignmentDraftDiffers
                          ? `Not saved yet. Still assigned to ${
                              currentTerminal.assignment?.vendorName || "the previous vendor"
                            } until you save.`
                          : profileExplanation}
                      </small>
                    </div>
                  )}
                  {!draft.assignment.vendorUserId && assignmentDraftDiffers && (
                    <div className="pos-fleet-current-assignment is-unsaved">
                      <span>Unassign</span>
                      <strong>No vendor</strong>
                      <small>
                        Not saved yet. Still assigned to{" "}
                        {currentTerminal.assignment?.vendorName || "the previous vendor"}{" "}
                        until you save.
                      </small>
                    </div>
                  )}

                  <label className="pos-fleet-vendor-search">
                    <span>
                      {draft.assignment.vendorUserId
                        ? "Reassign to another vendor"
                        : "Find a vendor"}
                    </span>
                    <div>
                      <input
                        value={vendorQuery}
                        onChange={(event) => setVendorQuery(event.target.value)}
                        placeholder={
                          assignmentLock?.remedy === "CLOSE_SHIFT"
                            ? "Close the open shift first"
                            : "Search vendor name or phone"
                        }
                        disabled={assignmentLock?.remedy === "CLOSE_SHIFT"}
                      />
                      {vendorSearching && <Spinner animation="border" size="sm" />}
                    </div>
                  </label>
                  {vendorResults.length > 0 && (
                    <div className="pos-fleet-vendor-results">
                      {vendorResults.map((vendor) => (
                        <button
                          type="button"
                          key={vendor.id}
                          onClick={() => chooseVendor(vendor)}
                        >
                          <strong>{vendor.label}</strong>
                          <span>{vendor.subLabel}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {draft.assignment.vendorUserId && (
                    <div className="pos-fleet-field-grid">
                      <ProfileField
                        value={draft.assignment.terminalProfile}
                        profiles={availableProfiles}
                        vendorTypeLabel={selectedTypeMetadata?.label}
                        onChange={(value) =>
                          patchDraft(["assignment", "terminalProfile"], value)
                        }
                      />
                      {selectedVendorType &&
                        selectedVendorType !== "EVENT_ORGANIZER" &&
                        (vendorOutletsLoading || vendorOutlets.length > 0) && (
                        <label>
                          <span>Outlet (optional)</span>
                          <AppSelect
                            value={draft.assignment.outletId}
                            onChange={(event) =>
                              patchDraft(
                                ["assignment", "outletId"],
                                event.target.value
                              )
                            }
                            disabled={vendorOutletsLoading}
                          >
                            <option value="">
                              {vendorOutletsLoading
                                ? "Loading outlets…"
                                : "All outlets"}
                            </option>
                            {vendorOutlets.map((outlet) => (
                              <option key={outlet.id} value={outlet.id}>
                                {outlet.name}
                              </option>
                            ))}
                          </AppSelect>
                          <small>
                            Scopes this terminal's catalog and order feed to one
                            branch. Leave on "All outlets" for a single-site
                            vendor.
                          </small>
                        </label>
                      )}
                    </div>
                  )}
                </section>

                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <div>
                      <h3>Desired configuration</h3>
                      <p>Applied automatically after the next heartbeat.</p>
                    </div>
                  </div>
                  <ToggleRow
                    title="Kiosk lock"
                    hint="Keep the terminal pinned to Lusuku POS."
                    checked={draft.desiredConfig.kioskLocked}
                    onChange={(value) =>
                      patchDraft(["desiredConfig", "kioskLocked"], value)
                    }
                  />
                  <ToggleRow
                    title="Automatic receipt printing"
                    hint={
                      selectedVendorType === "RESTAURANT"
                        ? "Print when an incoming order is accepted."
                        : "Print when checkout completes."
                    }
                    checked={draft.desiredConfig.autoPrintReceipts}
                    onChange={(value) =>
                      patchDraft(["desiredConfig", "autoPrintReceipts"], value)
                    }
                    disabled={selectedVendorType === "EVENT_ORGANIZER"}
                    reason={receiptPrintingBlockReason(selectedVendorType)}
                  />
                  <ToggleRow
                    title="Order fulfilment"
                    hint="Accept, prepare and hand off incoming online orders on the terminal."
                    checked={draft.desiredConfig.allowOrderFulfillment}
                    onChange={(value) =>
                      patchDraft(["desiredConfig", "allowOrderFulfillment"], value)
                    }
                    disabled={!ORDER_FULFILMENT_VENDOR_TYPES.includes(selectedVendorType)}
                    reason={orderFulfilmentBlockReason(
                      selectedVendorType,
                      selectedTypeMetadata?.label
                    )}
                  />
                  <ToggleRow
                    title="Ticket validation"
                    hint="Enable event ticket scanning and gate admission."
                    checked={draft.desiredConfig.allowTicketScanning}
                    onChange={(value) =>
                      patchDraft(["desiredConfig", "allowTicketScanning"], value)
                    }
                    disabled={selectedVendorType !== "EVENT_ORGANIZER"}
                    reason={ticketValidationBlockReason(selectedVendorType)}
                  />
                  {selectedVendorType === "EVENT_ORGANIZER" && (
                    <>
                      {/*
                        The backend for counter selling is real — five routes, a
                        shared hold pipeline, four test suites — but there is no
                        selling screen in the terminal build, so enabling this
                        would advertise a screen that does not exist and send a
                        cashier looking for it mid-shift. It stays disabled and
                        says so, rather than looking like a setting somebody
                        forgot to switch on. See WIRING_provisioning.md.

                        The row is no longer hard-wired off, though: THIS
                        terminal's own device report decides. The server refuses
                        the flag with 409 POS_CAPABILITY_NOT_REPORTED until the
                        build says it can sell, so the console and the API now
                        answer the same question the same way, and the day the
                        box-office screen ships the row opens by itself.
                      */}
                      <ToggleRow
                        title={
                          ticketSalesSupported
                            ? "Ticket sales"
                            : "Ticket sales — pending terminal build"
                        }
                        hint={
                          ticketSalesSupported
                            ? "This terminal's build reports box-office support. Cashiers can sell tickets at the counter."
                            : "Box-office selling is not in this terminal's build yet. Gate validation above is the supported event workflow today."
                        }
                        tooltip={
                          ticketSalesSupported
                            ? undefined
                            : "The server side is built and tested, but this terminal has not reported the TICKET_SALES capability. It unlocks on the release that adds the box-office screen."
                        }
                        checked={
                          ticketSalesSupported &&
                          draft.desiredConfig.allowTicketSales === true
                        }
                        onChange={(value) =>
                          patchDraft(["desiredConfig", "allowTicketSales"], value)
                        }
                        disabled={!ticketSalesSupported}
                      />
                      <div className="pos-fleet-field-grid with-top-gap">
                        <label>
                          <span>Gate label (required)</span>
                          <input
                            type="text"
                            maxLength={40}
                            required
                            placeholder="e.g. Gate 3 — Screen 2"
                            value={draft.desiredConfig.gateLabel}
                            onChange={(event) =>
                              patchDraft(
                                ["desiredConfig", "gateLabel"],
                                event.target.value
                              )
                            }
                          />
                          <small>
                            Pushed to the terminal and stamped on every scan.
                            Name each door once here — a label the steward types
                            in produces "Main", "" and "screen2" across one
                            evening and nothing can be reconciled by door.
                          </small>
                          {gateLabelMissing && (
                            <small style={{ color: "#b3261e", fontWeight: 650 }}>
                              An event terminal cannot be saved without a gate
                              label.
                            </small>
                          )}
                          {/*
                            The reported value, which the console never showed.
                            Ops could type a label, watch the row go green and
                            never learn whether it reached the door — the whole
                            failure E3 describes.
                          */}
                          <small>
                            Terminal reports:{" "}
                            <strong>
                              {currentTerminal?.reportedState?.gateLabel
                                ? currentTerminal.reportedState.gateLabel
                                : "not reported yet"}
                            </strong>
                            {currentTerminal?.reportedState?.gateLabel &&
                            currentTerminal.reportedState.gateLabel !==
                              draft.desiredConfig.gateLabel
                              ? " — differs from the label above, so the push has not landed."
                              : ""}
                          </small>
                        </label>
                      </div>
                    </>
                  )}
                  {/*
                    serviceState is CONFIG, not a command, and the difference
                    matters: a command evaporates at the next power cut, so a
                    terminal taken out of service by a command would quietly
                    start selling again after someone unplugged it — the worst
                    possible failure for this control, because nobody is
                    watching for it.
                  */}
                  <div className="pos-fleet-service-state with-top-gap">
                    <ToggleRow
                      title={
                        serviceStateBlockedReason
                          ? "Out of service — unavailable on this build"
                          : "Out of service"
                      }
                      hint={
                        serviceStateBlockedReason ||
                        "Show a full-screen notice and refuse sales. The terminal keeps checking in, so you can put it back from here."
                      }
                      tooltip={serviceStateBlockedReason || undefined}
                      checked={draft.desiredConfig.serviceState === "OUT_OF_SERVICE"}
                      disabled={Boolean(serviceStateBlockedReason)}
                      onChange={(value) =>
                        patchDraft(
                          ["desiredConfig", "serviceState"],
                          value ? "OUT_OF_SERVICE" : "IN_SERVICE"
                        )
                      }
                    />
                    {draft.desiredConfig.serviceState === "OUT_OF_SERVICE" && (
                      <label className="pos-fleet-wizard-field">
                        <span>What the screen should say</span>
                        <input
                          type="text"
                          maxLength={140}
                          placeholder="e.g. Till closed for stock take — use Till 2"
                          value={draft.desiredConfig.outOfServiceMessage}
                          onChange={(event) =>
                            patchDraft(
                              ["desiredConfig", "outOfServiceMessage"],
                              event.target.value
                            )
                          }
                        />
                        <small>
                          Whoever walks up to this terminal reads this and
                          nothing else. Say what to do instead, not just that
                          it is unavailable.
                        </small>
                      </label>
                    )}
                  </div>
                  <div className="pos-fleet-field-grid with-top-gap">
                    <label>
                      <span>Heartbeat interval</span>
                      <AppSelect
                        value={draft.desiredConfig.heartbeatIntervalSeconds}
                        onChange={(event) =>
                          patchDraft(
                            ["desiredConfig", "heartbeatIntervalSeconds"],
                            Number(event.target.value)
                          )
                        }
                      >
                        <option value={30}>30 seconds</option>
                        <option value={60}>1 minute</option>
                        <option value={120}>2 minutes</option>
                        <option value={300}>5 minutes</option>
                      </AppSelect>
                    </label>
                    <label>
                      <span>Update channel</span>
                      <AppSelect
                        value="PRODUCTION"
                        onChange={(event) =>
                          patchDraft(
                            ["desiredConfig", "updateChannel"],
                            event.target.value
                          )
                        }
                        disabled
                        title="Pilot releases are not configured yet."
                      >
                        <option value="PRODUCTION">Production</option>
                      </AppSelect>
                    </label>
                  </div>
                </section>

                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <div>
                      <h3>Remote actions</h3>
                      <p>
                        One-shot acts, queued and delivered on the next
                        heartbeat. Anything that must survive a reboot is
                        configuration above, not an action here.
                      </p>
                    </div>
                  </div>

                  {["ROUTINE", "SERVICE"].map((group) => {
                    const groupCommands = commandCatalog.filter(
                      (entry) => entry.group === group
                    );
                    if (groupCommands.length === 0) return null;
                    return (
                      <div key={group} className="pos-fleet-command-group">
                        <h4>
                          {group === "ROUTINE" ? "Routine" : "Service"}
                          <small>
                            {group === "ROUTINE"
                              ? "Safe at any time, on any build"
                              : "Interrupts what the terminal is doing"}
                          </small>
                        </h4>
                        <div className="pos-fleet-command-grid">
                          {groupCommands.map((entry) => {
                            const reason = commandBlockReason(
                              entry,
                              currentTerminal
                            );
                            return (
                              <button
                                type="button"
                                key={entry.type}
                                className={
                                  group === "SERVICE" ? "is-service" : undefined
                                }
                                onClick={() => sendCommand(entry.type)}
                                disabled={Boolean(commandBusy) || Boolean(reason)}
                                title={reason || entry.hint}
                              >
                                <strong>{entry.label}</strong>
                                <span>{reason || entry.hint}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  <details className="pos-fleet-danger-zone">
                    <summary>Credential — destructive</summary>
                    <p>
                      The terminal&apos;s <code>posd_</code> credential is
                      separate from any vendor password, which is what makes
                      remote reassignment possible. Rotating it invalidates the
                      old token the instant this returns — there is no grace
                      window and no second live credential.
                    </p>
                    <dl className="pos-fleet-diagnostics">
                      <div>
                        <dt>Token</dt>
                        <dd>
                          {currentTerminal.tokenSuffix
                            ? `…${currentTerminal.tokenSuffix}`
                            : "Revoked"}
                        </dd>
                      </div>
                      <div>
                        <dt>Last rotated</dt>
                        <dd>{fullDate(currentTerminal.tokenRotatedAt)}</dd>
                      </div>
                    </dl>
                    {revealedToken && (
                      <div className="pos-fleet-guide-callout is-warning">
                        <strong>Shown once — copy it into the terminal now</strong>
                        <CodeDisplay code={revealedToken} />
                        <span>
                          Nothing on the server can retrieve this again. If you
                          close this panel without entering it, rotate again.
                        </span>
                      </div>
                    )}
                    <div className="pos-fleet-command-grid">
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => rotateCredential(false)}
                        disabled={Boolean(credentialBusy)}
                      >
                        <strong>
                          {credentialBusy === "rotate" ? "Rotating…" : "Rotate credential"}
                        </strong>
                        <span>Issue a new token, shown once. The device must be re-entered.</span>
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => rotateCredential(true)}
                        disabled={Boolean(credentialBusy)}
                      >
                        <strong>
                          {credentialBusy === "revoke" ? "Revoking…" : "Revoke credential"}
                        </strong>
                        <span>
                          Burn the token without a replacement and suspend the
                          terminal — the right answer to a leak.
                        </span>
                      </button>
                    </div>
                  </details>

                  {currentTerminal.commands?.length > 0 && (
                    <div className="pos-fleet-command-timeline with-top-gap">
                      <h4>Command history</h4>
                      {currentTerminal.commands.slice(0, 8).map((command) => (
                        <div key={command.commandId}>
                          <span
                            className={`pos-fleet-audit-dot tone-${String(
                              command.status
                            ).toLowerCase()}`}
                          />
                          <div>
                            <strong>{profileLabel(command.type)}</strong>
                            <span>
                              {profileLabel(command.status)}
                              {" · "}
                              {relativeTime(
                                command.acknowledgedAt ||
                                  command.cancelledAt ||
                                  command.deliveredAt ||
                                  command.createdAt
                              )}
                              {command.resultMessage
                                ? ` · ${command.resultMessage}`
                                : ""}
                            </span>
                          </div>
                          {["PENDING", "DELIVERED"].includes(command.status) && (
                            <button
                              type="button"
                              className="pos-fleet-clear"
                              onClick={() => cancelCommand(command.commandId)}
                              disabled={commandBusy === command.commandId}
                            >
                              {commandBusy === command.commandId
                                ? "Cancelling…"
                                : "Cancel"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <h3>Reported device state</h3>
                  </div>
                  <dl className="pos-fleet-diagnostics">
                    <div>
                      <dt>Hardware</dt>
                      <dd>{currentTerminal.reportedState?.model || "Unknown"}</dd>
                    </div>
                    <div>
                      <dt>App</dt>
                      <dd>
                        {currentTerminal.reportedState?.appVersionName || "Unknown"}{" "}
                        ({currentTerminal.reportedState?.appVersionCode || "—"})
                      </dd>
                    </div>
                    <div>
                      <dt>Battery</dt>
                      <dd>
                        {currentTerminal.reportedState?.batteryLevel == null
                          ? "Unknown"
                          : `${currentTerminal.reportedState.batteryLevel}%`}
                        {currentTerminal.reportedState?.charging ? " · charging" : ""}
                      </dd>
                    </div>
                    <div>
                      <dt>Network</dt>
                      <dd>
                        {currentTerminal.reportedState?.networkType || "Unknown"} ·{" "}
                        {currentTerminal.reportedState?.ipAddress || "No IP"}
                      </dd>
                    </div>
                    <div>
                      <dt>Internet</dt>
                      <dd>
                        {currentTerminal.reportedState?.internetValidated
                          ? "Validated"
                          : "Not validated"}
                        {" · "}
                        {currentTerminal.reportedState?.realtimeConnected
                          ? "Realtime connected"
                          : "Realtime disconnected"}
                      </dd>
                    </div>
                    <div>
                      <dt>Printer</dt>
                      <dd>
                        {currentTerminal.reportedState?.printerReady === true
                          ? "Ready"
                          : currentTerminal.reportedState?.printerReady === false
                            ? "Unavailable"
                            : "Not reported"}
                      </dd>
                    </div>
                    <div>
                      <dt>Device owner</dt>
                      <dd>
                        {currentTerminal.reportedState?.deviceOwner ? "Active" : "Not active"}
                      </dd>
                    </div>
                    <div>
                      <dt>Kiosk</dt>
                      <dd>
                        {currentTerminal.reportedState?.kioskLocked ? "Locked" : "Unlocked"}
                      </dd>
                    </div>
                    <div>
                      <dt>Last heartbeat</dt>
                      <dd>{fullDate(currentTerminal.lastHeartbeatAt)}</dd>
                    </div>
                    <div>
                      <dt>Pending sync</dt>
                      <dd>{currentTerminal.reportedState?.pendingSyncCount || 0}</dd>
                    </div>
                    <div>
                      <dt>Applied receipt mode</dt>
                      <dd>
                        {currentTerminal.reportedState?.autoPrintReceipts == null
                          ? "Not reported"
                          : currentTerminal.reportedState.autoPrintReceipts
                            ? "Automatic"
                            : "Manual"}
                      </dd>
                    </div>
                    <div>
                      <dt>Applied heartbeat</dt>
                      <dd>
                        {currentTerminal.reportedState
                          ?.heartbeatIntervalSeconds == null
                          ? "Not reported"
                          : `${currentTerminal.reportedState.heartbeatIntervalSeconds}s`}
                      </dd>
                    </div>
                    <div>
                      <dt>Shift</dt>
                      <dd>
                        {currentTerminal.reportedState?.activeShift
                          ? `Open · ${
                              currentTerminal.reportedState?.cashierName ||
                              "Cashier"
                            }`
                          : "Closed"}
                      </dd>
                    </div>
                    {/*
                      E3: the console pushed a gate label and never showed what
                      came back, so ops typed "Gate 3 — Screen 2", saw green,
                      and had no way to learn whether the door was ever named.
                      This row is that answer.
                    */}
                    {currentTerminal.assignment?.terminalProfile ===
                      "EVENT_ACCESS" && (
                      <div>
                        <dt>Gate label</dt>
                        <dd>
                          {currentTerminal.reportedState?.gateLabel ||
                            "Not reported"}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt>Build capabilities</dt>
                      <dd>
                        {(currentTerminal.reportedState?.capabilities || [])
                          .length
                          ? currentTerminal.reportedState.capabilities
                              .map((capability) => profileLabel(capability))
                              .join(" · ")
                          : "None reported"}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section className="pos-fleet-form-section">
                  <div className="pos-fleet-section-title">
                    <h3>Recent activity</h3>
                  </div>
                  <div className="pos-fleet-audit">
                    {auditRows.length === 0 ? (
                      <span>No audit events yet.</span>
                    ) : (
                      auditRows.slice(0, 12).map((entry) => (
                        <div key={entry._id}>
                          <span className="pos-fleet-audit-dot" />
                          <div>
                            <strong>{profileLabel(entry.action)}</strong>
                            <span>
                              {entry.actorUserId?.name ||
                                entry.actorUserId?.email ||
                                entry.source}
                              {" · "}
                              {relativeTime(entry.createdAt)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>
            ) : null}

            {draft && (
              <div className="pos-fleet-inspector-footer">
                <span>
                  Desired config v{currentTerminal?.configVersion || 1}
                </span>
                <button
                  type="button"
                  onClick={() => saveTerminal()}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default PosTerminalFleetView;
